import { cn } from '@/ui/lib/utils'

export interface QuantLiveEventRowProps {
  seq: number
  /** Local `HH:MM:SS`, or a dash. */
  time: string
  /** The one-line summary; the full text is the hover title. */
  text: string
  kind: 'plain' | 'feed' | 'reject' | 'roll'
  /** An unknown event type: shown dimmed. */
  muted: boolean
}

const GLYPH: Record<'feed' | 'reject' | 'roll', string> = { feed: '◆', reject: '✕', roll: '↻' }

const KIND_CLASS: Record<QuantLiveEventRowProps['kind'], string> = {
  plain: 'text-paper-90',
  feed: 'text-[#CB8A00]',
  reject: 'text-alert',
  roll: 'border-l-2 border-accent-line text-paper-90',
}

/**
 * One session event (spec 041): a fixed 28 px row in a 56 px | 72 px | 1fr grid. The sequence
 * number is muted, the summary is a single truncating line, and three kinds are visibly
 * distinct: a feed change (amber, `◆`), a rejected order (alert red, `✕`) and a contract roll
 * (accent left border, `↻`). The glyphs are decorative and hidden from assistive technology.
 */
export function QuantLiveEventRow({ seq, time, text, kind, muted }: QuantLiveEventRowProps) {
  return (
    <li
      data-event-kind={kind}
      data-event-seq={seq}
      title={text}
      className={cn(
        'box-border grid h-[28px] grid-cols-[56px_72px_1fr] items-center gap-x-2 border-b border-paper-14 px-3 font-mono text-[11px]',
        KIND_CLASS[kind],
        muted && 'opacity-60',
      )}
    >
      <span className="truncate text-paper-40">{seq}</span>
      <span className="truncate text-paper-50">{time}</span>
      <span className="min-w-0 truncate">
        {kind !== 'plain' && (
          <span aria-hidden="true" className="mr-1.5">
            {GLYPH[kind]}
          </span>
        )}
        {text}
      </span>
    </li>
  )
}
